/**
 * Mermaid Tool - 匯出功能
 * static/js/mermaid/export.js
 */

import { showToast, copyToClipboard } from './ui.js';

/**
 * 匯出圖表為 PNG
 * @param {HTMLElement} container - 包含 SVG 的容器
 * @param {string} title - 檔案名稱
 * @param {number} scale - 縮放倍率
 * @returns {Promise<boolean>} 是否成功
 */
export async function exportPng(container, title = 'diagram', scale = 3) {
    const svg = container.querySelector('svg');
    if (!svg) {
        showToast('請先生成圖表', 'warning');
        return false;
    }
    
    try {
        // 複製 SVG
        const svgClone = svg.cloneNode(true);
        
        // 設定背景色
        svgClone.style.backgroundColor = '#ffffff';
        
        // 取得尺寸
        const svgWidth = svg.viewBox?.baseVal?.width || svg.width?.baseVal?.value || svg.getBoundingClientRect().width;
        const svgHeight = svg.viewBox?.baseVal?.height || svg.height?.baseVal?.value || svg.getBoundingClientRect().height;
        
        // 建立 canvas
        const canvas = document.createElement('canvas');
        canvas.width = svgWidth * scale;
        canvas.height = svgHeight * scale;
        
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // 轉換 SVG 為 data URL
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        // 載入圖片
        const img = new Image();
        
        return new Promise((resolve) => {
            img.onload = () => {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                URL.revokeObjectURL(svgUrl);
                
                // 下載
                const link = document.createElement('a');
                link.download = `${sanitizeFilename(title)}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
                
                showToast('PNG 已下載', 'success');
                resolve(true);
            };
            
            img.onerror = () => {
                URL.revokeObjectURL(svgUrl);
                showToast('PNG 匯出失敗', 'danger');
                resolve(false);
            };
            
            img.src = svgUrl;
        });
        
    } catch (error) {
        console.error('Export PNG error:', error);
        showToast('PNG 匯出失敗: ' + error.message, 'danger');
        return false;
    }
}

/**
 * 匯出圖表為 SVG
 * @param {HTMLElement} container - 包含 SVG 的容器
 * @param {string} title - 檔案名稱
 * @returns {boolean} 是否成功
 */
export function exportSvg(container, title = 'diagram') {
    const svg = container.querySelector('svg');
    if (!svg) {
        showToast('請先生成圖表', 'warning');
        return false;
    }
    
    try {
        // 複製並設定背景
        const svgClone = svg.cloneNode(true);
        svgClone.style.backgroundColor = '#ffffff';
        
        // 序列化
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        
        // 下載
        const link = document.createElement('a');
        link.download = `${sanitizeFilename(title)}.svg`;
        link.href = url;
        link.click();
        
        URL.revokeObjectURL(url);
        showToast('SVG 已下載', 'success');
        return true;
        
    } catch (error) {
        console.error('Export SVG error:', error);
        showToast('SVG 匯出失敗: ' + error.message, 'danger');
        return false;
    }
}

/**
 * 複製程式碼到剪貼簿
 * @param {string} code - Mermaid 程式碼
 * @returns {Promise<boolean>} 是否成功
 */
export async function copyCode(code) {
    if (!code) {
        showToast('沒有程式碼可複製', 'warning');
        return false;
    }
    
    const success = await copyToClipboard(code);
    if (success) {
        showToast('程式碼已複製', 'success');
    } else {
        showToast('複製失敗', 'danger');
    }
    return success;
}

/**
 * 獲取匯出縮放倍率
 * @returns {number} 縮放倍率
 */
export function getExportScale() {
    const scaleSelect = document.getElementById('exportScale');
    return scaleSelect ? parseInt(scaleSelect.value, 10) : 3;
}

/**
 * 清理檔案名稱（移除不安全字元）
 * @param {string} filename - 原始檔案名稱
 * @returns {string} 清理後的檔案名稱
 */
export function sanitizeFilename(filename) {
    if (!filename) return 'diagram';
    
    const cleaned = filename
        .trim()
        .replace(/[<>:"/\\|?*]/g, '_')  // 不合法字元換成底線
        .replace(/\s+/g, '_')            // 空白換成底線
        .replace(/_+/g, '_')             // 多個底線合併
        .replace(/^_|_$/g, '')           // 移除開頭結尾的底線
        .substring(0, 100);              // 限制長度
    
    return cleaned || 'diagram';
}
